import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@hooks/useAuth';
import { UserRole } from '@shared/types/user';
import { ROUTES } from '@lib/routes';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
}

/**
 * ProtectedRoute component that enforces role-based access control
 * Redirects unauthenticated users to login
 * Redirects authenticated users without required role to home
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo,
}: ProtectedRouteProps): JSX.Element {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={redirectTo || ROUTES.LOGIN}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check if user has required role
  if (!allowedRoles.includes(user.role)) {
    // Redirect based on user's actual role
    const roleRedirectMap: Record<UserRole, string> = {
      [UserRole.USER]: ROUTES.CUSTOMER.BASE,
      [UserRole.PARTNER]: ROUTES.PARTNER.BASE,
      [UserRole.ADMIN]: ROUTES.ADMIN.BASE,
    };

    return (
      <Navigate
        to={redirectTo || roleRedirectMap[user.role] || ROUTES.HOME}
        replace
      />
    );
  }

  return <>{children}</>;
}

